import { useEffect, useState } from "react";
import { IconUp, IconPlus } from "../layout/Icons";
import { TaskRail, useProjectTasks, type ProjectTask } from "./TaskRail";

/**
 * Full-screen Kanban for the project's background tasks. Opens over
 * the editor when the user clicks "Open task board" in the TaskRail.
 * Columns: Drafts / Active / Ready / Done. Mock-wired: drag-drop is
 * intentionally out of scope (Replit's empty state doesn't exercise
 * it and the value's in the column tally).
 */
const COLUMNS: ReadonlyArray<{ id: ProjectTask["column"]; label: string }> = [
  { id: "draft", label: "Drafts" },
  { id: "active", label: "Active" },
  { id: "ready", label: "Ready" },
  { id: "done", label: "Done" },
];

export function TaskBoard({
  projectId,
  projectName,
  onClose,
}: {
  projectId: string;
  projectName: string;
  onClose: () => void;
}) {
  const { tasks, add, move, remove } = useProjectTasks(projectId);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState("");
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<ProjectTask["column"] | null>(null);

  // Escape closes the overlay — same convention as command palettes.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = query.trim()
    ? tasks.filter((t) => t.prompt.toLowerCase().includes(query.trim().toLowerCase()))
    : tasks;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Background tasks board"
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--bg-canvas)",
        color: "var(--text-primary)",
        display: "flex",
        zIndex: 50,
      }}
    >
      {/* Left rail (slimmed re-use of TaskRail to match Replit's layout).
          boardOpen=true flips the rail's bottom button to "Close task
          board" and routes the click to onClose. */}
      <div
        style={{
          flexShrink: 0,
          borderRight: "1px solid var(--border-default)",
          display: "flex",
          flexDirection: "column",
          background: "var(--bg-panel)",
        }}
      >
        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
          <TaskRail
            projectId={projectId}
            projectName={projectName}
            onOpenBoard={onClose}
            boardOpen
          />
        </div>
      </div>

      {/* Board */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 18px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500 }}>{projectName}</span>
          <div style={{ flex: 1 }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tasks…"
            style={{
              width: 260,
              padding: "6px 10px",
              background: "var(--bg-canvas)",
              border: "1px solid var(--border-default)",
              borderRadius: 7,
              color: "var(--text-primary)",
              fontSize: 12,
              outline: "none",
            }}
          />
        </header>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 1,
            background: "var(--border-default)",
          }}
        >
          {COLUMNS.map((col) => {
            const items = filtered.filter((t) => t.column === col.id);
            const isDropTarget = dragOverCol === col.id;
            return (
              <section
                key={col.id}
                data-testid={`column-${col.id}`}
                onDragOver={(e) => {
                  // Required for onDrop to fire.
                  if (draggingId) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }
                }}
                onDragEnter={(e) => {
                  if (draggingId) {
                    e.preventDefault();
                    setDragOverCol(col.id);
                  }
                }}
                onDragLeave={(e) => {
                  // Only clear when leaving the section itself, not entering a child.
                  if (e.currentTarget === e.target) setDragOverCol(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData("text/x-vorloop-task");
                  if (id) move(id, col.id);
                  setDraggingId(null);
                  setDragOverCol(null);
                }}
                style={{
                  background: isDropTarget ? "var(--bg-panel-2)" : "var(--bg-canvas)",
                  outline: isDropTarget ? "2px dashed var(--accent-blue)" : "none",
                  outlineOffset: -2,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  padding: "12px 14px",
                  transition: "background 100ms",
                }}
              >
                <header style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{col.label}</span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{items.length}</span>
                </header>
                <ol
                  style={{
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  {items.length === 0 ? (
                    <li
                      style={{
                        padding: "20px 12px",
                        color: "var(--text-tertiary)",
                        fontSize: 12,
                        textAlign: "center",
                      }}
                    >
                      No tasks
                    </li>
                  ) : (
                    items.map((t) => {
                      const isDragging = draggingId === t.id;
                      return (
                        <li
                          key={t.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/x-vorloop-task", t.id);
                            setDraggingId(t.id);
                          }}
                          onDragEnd={() => {
                            setDraggingId(null);
                            setDragOverCol(null);
                          }}
                          style={{
                            padding: "8px 10px",
                            background: "var(--bg-panel)",
                            border: "1px solid var(--border-default)",
                            borderRadius: 8,
                            fontSize: 12,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                            cursor: "grab",
                            opacity: isDragging ? 0.4 : 1,
                            transition: "opacity 80ms",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                            {t.column === "active" && (
                              <span
                                aria-label="Task running"
                                data-testid="task-running"
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: 999,
                                  border: "2px solid var(--accent-blue)",
                                  borderTopColor: "transparent",
                                  display: "inline-block",
                                  animation: "vp-spin 0.8s linear infinite",
                                  flexShrink: 0,
                                  marginTop: 3,
                                }}
                              />
                            )}
                            <span style={{ flex: 1 }}>{t.prompt}</span>
                          </div>
                          {t.lastError && (
                            <div
                              data-testid="task-error"
                              title={t.lastError}
                              style={{
                                padding: "4px 8px",
                                fontSize: 11,
                                background: "rgba(248,113,113,0.12)",
                                border: "1px solid rgba(248,113,113,0.35)",
                                borderRadius: 6,
                                color: "var(--accent-red)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ⚠ Failed: {t.lastError}
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 4 }}>
                            {COLUMNS.filter((c) => c.id !== t.column).map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => move(t.id, c.id)}
                                style={{
                                  padding: "2px 6px",
                                  fontSize: 10,
                                  background: "transparent",
                                  border: "1px solid var(--border-default)",
                                  borderRadius: 5,
                                  color: "var(--text-tertiary)",
                                  cursor: "pointer",
                                }}
                              >
                                → {c.label}
                              </button>
                            ))}
                            <div style={{ flex: 1 }} />
                            <button
                              type="button"
                              onClick={() => remove(t.id)}
                              style={{
                                padding: "2px 6px",
                                fontSize: 10,
                                background: "transparent",
                                border: "none",
                                color: "var(--accent-red)",
                                cursor: "pointer",
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      );
                    })
                  )}
                </ol>
              </section>
            );
          })}
        </div>

        {/* Plan a new task */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) {
              add(draft);
              setDraft("");
            }
          }}
          style={{
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
            padding: 14,
          }}
        >
          <div
            style={{
              maxWidth: 760,
              margin: "0 auto",
              padding: "8px 10px",
              background: "var(--bg-panel-2)",
              border: "1px solid var(--border-default)",
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <button
              type="button"
              aria-label="Add"
              style={{
                width: 26,
                height: 26,
                background: "transparent",
                border: "1px solid var(--border-default)",
                borderRadius: 7,
                color: "var(--text-secondary)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconPlus size={12} />
            </button>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Plan a new task…"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: "var(--text-primary)",
                fontSize: 13,
              }}
            />
            <button
              type="submit"
              disabled={!draft.trim()}
              aria-label="Submit task"
              style={{
                width: 28,
                height: 28,
                background: draft.trim() ? "var(--accent-blue)" : "var(--bg-active)",
                color: draft.trim() ? "#fff" : "var(--text-tertiary)",
                border: "none",
                borderRadius: 8,
                cursor: draft.trim() ? "pointer" : "not-allowed",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconUp />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
